import { useApprovingInvoiceReportSummary } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { usePaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const statusCodesCheckAccessPermission = ['401', '403', '404'];

export const useApprovingInvoiceReportSummaryData = (
  id: string,
  onClose: () => void
) => {
  const { data = {}, error } = useApprovingInvoiceReportSummary(id);
  const { setTitleInfo } = usePaymentDetail();
  const notify = useToastAPI();
  const { t } = useTranslation();
  const refetchApprovingInvoiceReportsSearch =
    useApprovingInvoiceReportsSearchRefetch();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();

  const showErrorMessages = useCallback(
    (message?: string) => {
      notify.error({
        title: message || t('system_error'),
        duration: 5,
      });
    },
    [notify, t]
  );

  const onCheckAccessApprovingReportSuccess = useCallback(
    (message?: string) => () => {
      showErrorMessages(message);
      onClose();
    },
    [showErrorMessages, onClose]
  );

  const onCheckAccessApprovingReportFailure = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    setTitleInfo({
      title: data?.title ?? '',
      number: data?.number ?? '',
      is_report_approval: data?.is_report_approval ?? false,
      is_editable: data?.is_editable ?? false,
      is_approvable: data?.is_approvable ?? false,
    });
  }, [
    data?.title,
    data?.number,
    data?.is_report_approval,
    data?.is_editable,
    data?.is_approvable,
    setTitleInfo,
  ]);

  useEffect(() => {
    if (error) {
      if (statusCodesCheckAccessPermission.includes(error?.code || '')) {
        handleCheckPermission({
          fnCheckPermission: refetchApprovingInvoiceReportsSearch,
          onCheckSuccess: onCheckAccessApprovingReportSuccess(
            error?.messages?.join?.('')
          ),
          onCheckFailure: onCheckAccessApprovingReportFailure,
        });
      }
    }
  }, [
    error,
    handleCheckPermission,
    refetchApprovingInvoiceReportsSearch,
    onCheckAccessApprovingReportSuccess,
    onCheckAccessApprovingReportFailure,
  ]);

  return { data, error };
};
